import Logger from "./Logger";
import g from "./globals";

const logger = new Logger(__filename);

const NESTED_TOKENS_RE = /{([^}]+)}/g;

/**
 * This class acts as a central point to collect and resolve locale data.
 * It can be instatiated with a dictionary following this structure:
 *
 * ```js
 *   {
 *     greet: {
 *       'es_ES': 'Hola mundo!',
 *       'en_GB': 'Hello world!',
 *       'en_GB@ES': 'Hello Spain!',
 *       'fr_FR@CA': 'Hello Canada!'
 *     },
 *     currency
 * : {
 *       'ES': '#,##0.00 ¤¤'
 *     },
 *     decimalSeparator: {
 *       'en_GB@ES': ',',
 *       'gl_ES@ES': '.'
 *     }
 *   }
 * ```
 *
 * Provided class methods `t` and `c` will follow then widely known `fallback
 * strategies` to resolve the required tokens:
 *
 * ```js
 * this.fallbacks = [ locale, country, `en_GB@${country}`, language, 'en_GB'];
 *  ```
 *
 * It can handle nested replacements or nested helper invokations during
 * retrieval.
 *
 * See `tests` for sample usage.
 */
class L10N {
  /**
   * @param dict
   * @param locale
   * @param helpers
   */
  constructor(dict, locale, helpers = {}) {
    /**
     * original dict
     */
    this.dict = dict;
    /**
     * @ignore
     */
    this.locale = locale;
    /**
     * @ignore
     */
    this.helpers = helpers;
    const [language, country] = this.locale.split("@");

    /**
     * @ignore
     */
    this.fallbacks = [locale, country, `en_GB@${country}`, language, "en_GB"];
  }

  /**
   * @ignore
   */
  resolve(content) {
    if (typeof content !== "string") content = content.toString();
    return content.replace(NESTED_TOKENS_RE, (match, interpolation) => {
      let replacement;
      const [token, value] = interpolation.split(" ");
      if (value) {
        const helper = this.helpers[token];
        if (typeof helper !== "function") {
          const error = content.replace(match, `>>${match}<<`);
          throw new Error(`unknown helper: ${error}`);
        }
        replacement = helper(this.c(value));
      } else {
        replacement = this.c(token);
      }
      return replacement;
    });
  }

  t(token, options = { showErrors: g.developerMode }) {
    let result = this.lookup(token, options);
    if (typeof result === "string" && !/-tmpl$|Tmpl$/i.test(token)) {
      result = this.resolve(result).replace(/\n/g, "<br>");
    } else if (typeof result === "object" || Array.isArray(result)) {
      return result;
    } else if ((options.showErrors && typeof result === "undefined") || typeof result === "undefined" || result === null) {
      logger.warn(`[[CONTEXT MISSING]]: ${token}.${this.locale}`);
      return "";
    } else {
      result = this.resolve(result);
    }
    return result;
  }

  c(token, options = { throwErrors: true }) {
    let result = this.lookup(token, options);
    if (typeof result === "string") {
      result = this.resolve(result).replace(/\n|\r/g, "<br />");
    }
    if ((options.throwErrors && typeof result === "undefined") || typeof result === "undefined") {
      logger.warn(`[[CONTEXT MISSING]]: ${token}.${this.locale}`);
      return "";
      // throw new Error(`context missing: ${token}.${this.locale}`);
    }
    return result;
  }

  /**
   * @ignore
   */
  lookup(token) {
    let value;
    for (const language of this.fallbacks) {
      value = Object.keys(this.dict).includes(token) ? this.dict[token] : (this.dict[token] || {})[language];
      if (typeof value !== "undefined" && value !== "") {
        break;
      }
      value = (this.dict[language] || {})[token];
      if (typeof value !== "undefined" && value !== "") {
        break;
      }
    }
    return value;
  }
  setup(dict) {
    if (dict.constructor === Array) {
      dict
        .map(key => {
          const newDictKey = {
            ...this.dict[key],
            ...dict[key],
          };
          this.dict[key] = newDictKey;
          return this.dict[key];
        })
        .bind(this);
    } else {
      Object.keys(dict).map(key => {
        if (dict[key] instanceof Object) {
          const newDictKey = {
            ...this.dict[key],
            ...dict[key],
          };
          this.dict[key] = newDictKey;
          return this.dict[key];
        }
        return (this.dict[key] = dict[key]);
      });
    }
  }
}

export default L10N;
